import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import {
  IOnboardingHealthTick,
  IOnboardingJobRole,
  IOnboardingOption,
  IOnboardingServices,
  IOnboardingStep2Context,
  IOnboardingWizardState,
} from "../../../Interfaces/IOnboardingWizardState";
import {
  COLLECTION_COLLECT_ONLY,
  COLLECT_ONLY_LAB_OPTIONS,
  defaultLabService,
  expandLevels,
  isCollectOnly,
  isLabServiceLocked,
  labsForMethod,
  labServiceLockReason,
  LAB_OPTION_ALWAYS_LAB,
  LAB_OPTION_LAB_IF_NON_NEG,
  LAB_SERVICE_CONFIRMATION_ONLY,
  newId,
  defaultLabForMethod,
  describeCopiedPreEmployment,
  tddaPreEmploymentSource,
} from "./Step2.rules";
import { optionMenuContent, renderOptionValue } from "./OptionMenu";

type Props = {
  state: IOnboardingWizardState;
  services: IOnboardingServices;
  healthTick: IOnboardingHealthTick;
  onChange: (healthTick: IOnboardingHealthTick) => void;
  context: IOnboardingStep2Context;
  frozen: boolean;
};

const emptyRole = (tests: string[]): IOnboardingJobRole => ({
  id: newId(),
  name: "",
  tests,
  notes: "",
});

/**
 * The HealthTick block (S2.14–S2.16, S2.22; stories 5624, 5625, 5626).
 *
 * Four services, two of them tiered with multi-select cumulative levels; each tiered service carries
 * its own job roles with the 21 defined tests; and the pre-employment drug test lives inside PEM
 * only. Level contents are provisional (decision 1) and live in reference data, so HealthTick's
 * definitive matrix can replace them without touching this screen.
 */
const HealthTickCard: React.FC<Props> = ({
  state,
  services,
  healthTick,
  onChange,
  context,
  frozen,
}) => {
  const references = state.references;
  const levelTests = context.healthTickLevelTests || {};

  // What Copy from TDDA pre-employment just did. It lives up here rather than in the block's render
  // function so it survives a re-render, and it is retired the moment any of the five copied fields
  // is edited — a confirmation that outlived the values it describes would be worse than none
  // (bug 5719).
  const [copyNotice, setCopyNotice] = React.useState<string | null>(null);

  const set = (changes: Partial<IOnboardingHealthTick>) =>
    onChange({ ...healthTick, ...changes });

  const toggleLevel = (service: "PEM" | "AHM", level: string, checked: boolean) => {
    const key = service === "PEM" ? "pemLevels" : "ahmLevels";
    const current = (service === "PEM" ? healthTick.pemLevels : healthTick.ahmLevels) || [];
    set({ [key]: checked ? [...current, level] : current.filter((x) => x !== level) } as any);
  };

  const roleTests = (service: "PEM" | "AHM") =>
    expandLevels(service === "PEM" ? healthTick.pemLevels : healthTick.ahmLevels, levelTests);

  const setRoles = (service: "PEM" | "AHM", roles: IOnboardingJobRole[]) =>
    set(service === "PEM" ? { pemRoles: roles } : { ahmRoles: roles });

  const renderTieredService = (service: "PEM" | "AHM") => {
    const isPem = service === "PEM";
    const selected = isPem ? healthTick.pemSelected : healthTick.ahmSelected;
    const levels = (isPem ? healthTick.pemLevels : healthTick.ahmLevels) || [];
    const roles = (isPem ? healthTick.pemRoles : healthTick.ahmRoles) || [];
    const customNotes = isPem ? healthTick.pemCustomNotes : healthTick.ahmCustomNotes;
    const implied = roleTests(service);
    const option = references.step2HealthTickServices.find((x) => x.key === service);

    return (
      <Paper elevation={0} sx={{ p: 2, mt: 1, border: "1px solid #eee" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              disabled={frozen}
              checked={selected}
              onChange={(e) =>
                set(isPem ? { pemSelected: e.target.checked } : { ahmSelected: e.target.checked })
              }
            />
          }
          label={option?.label || service}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          {option?.description}
        </Typography>

        {selected && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Levels — choose any combination. Standard includes Basic, Premium includes both.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {references.step2HealthTickLevels.map((level) => (
                <Tooltip key={level.key} title={level.description || ""}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        disabled={frozen}
                        checked={levels.includes(level.key)}
                        onChange={(e) => toggleLevel(service, level.key, e.target.checked)}
                      />
                    }
                    label={level.label}
                  />
                </Tooltip>
              ))}
            </Box>

            {levels.includes("Custom") && (
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Custom level notes"
                sx={{ mt: 1 }}
                disabled={frozen}
                value={customNotes ?? ""}
                onChange={(e) =>
                  set(
                    isPem
                      ? { pemCustomNotes: e.target.value }
                      : { ahmCustomNotes: e.target.value },
                  )
                }
              />
            )}

            {/* Provisional contents (decision 1) — clearly marked, and replaceable in one place. */}
            {implied.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  These levels imply {implied.length} tests. The level contents are{" "}
                  <strong>provisional</strong> until HealthTick supplies its definitive matrix.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Job roles ({roles.length}) — never required; a service can be sold before its roles
                are agreed.
              </Typography>
              <Box>
                <Button
                  size="small"
                  disabled={frozen}
                  onClick={() => setRoles(service, [...roles, emptyRole(implied)])}
                >
                  Add role
                </Button>
                <Button
                  size="small"
                  disabled={frozen || !roles.length}
                  onClick={() => {
                    const last = roles[roles.length - 1];
                    setRoles(service, [
                      ...roles,
                      { ...last, id: newId(), name: `${last.name} (copy)` },
                    ]);
                  }}
                >
                  Duplicate last
                </Button>
                {/* Only ever adds — a deliberate choice is never removed (S2.15). */}
                {roles.length > 0 && implied.length > 0 && (
                  <Button
                    size="small"
                    disabled={frozen}
                    onClick={() =>
                      setRoles(
                        service,
                        roles.map((role) => ({
                          ...role,
                          tests: Array.from(new Set([...(role.tests || []), ...implied])),
                        })),
                      )
                    }
                  >
                    Add level defaults
                  </Button>
                )}
              </Box>
            </Box>

            {roles.map((role, index) => (
              <Paper key={role.id || index} elevation={0} sx={{ p: 1, mt: 1, border: "1px solid #f0f0f0" }}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Role name"
                    sx={{ minWidth: 220 }}
                    disabled={frozen}
                    value={role.name ?? ""}
                    onChange={(e) =>
                      setRoles(
                        service,
                        roles.map((x, i) => (i === index ? { ...x, name: e.target.value } : x)),
                      )
                    }
                  />
                  {/* AHM roles carry a frequency; PEM roles have no such field and store none. */}
                  {!isPem && (
                    <TextField
                      select
                      size="small"
                      label="Frequency"
                      sx={{ minWidth: 220 }}
                      disabled={frozen}
                      value={role.frequency ?? ""}
                      onChange={(e) =>
                        setRoles(
                          service,
                          roles.map((x, i) =>
                            i === index ? { ...x, frequency: e.target.value } : x,
                          ),
                        )
                      }
                    >
                      {references.step2AhmFrequencies.map((frequency) => (
                        <MenuItem key={frequency} value={frequency}>
                          {frequency}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <Button
                    size="small"
                    color="error"
                    disabled={frozen}
                    onClick={() => setRoles(service, roles.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {references.step2HealthTickTests
                    .filter((test) => isPem || !test.pemOnly)
                    .map((test) => (
                      <FormControlLabel
                        key={test.key}
                        control={
                          <Checkbox
                            size="small"
                            disabled={frozen}
                            checked={(role.tests || []).includes(test.key)}
                            onChange={(e) =>
                              setRoles(
                                service,
                                roles.map((x, i) =>
                                  i === index
                                    ? {
                                        ...x,
                                        tests: e.target.checked
                                          ? [...(x.tests || []), test.key]
                                          : (x.tests || []).filter((t) => t !== test.key),
                                      }
                                    : x,
                                ),
                              )
                            }
                          />
                        }
                        label={<Typography variant="caption">{test.label}</Typography>}
                      />
                    ))}
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  label="Notes"
                  sx={{ mt: 0.5 }}
                  disabled={frozen}
                  value={role.notes ?? ""}
                  onChange={(e) =>
                    setRoles(
                      service,
                      roles.map((x, i) => (i === index ? { ...x, notes: e.target.value } : x)),
                    )
                  }
                />
              </Paper>
            ))}

            {/* The pre-employment drug test lives inside PEM only (S2.16). */}
            {isPem && renderHtPreEmployment()}
          </>
        )}
      </Paper>
    );
  };

  const renderHtPreEmployment = () => {
    const preEmployment =
      healthTick.htPreEmployment ?? {
        included: false,
        method: "Urine",
        collection: "Screen",
        labOption: LAB_OPTION_LAB_IF_NON_NEG,
        labService: LAB_SERVICE_CONFIRMATION_ONLY,
        confirmationLab: null,
        notes: null,
      };

    const setPre = (changes: Partial<typeof preEmployment>) =>
      set({ htPreEmployment: { ...preEmployment, ...changes } });

    /** An edit by hand, which retires any standing copy confirmation before writing. */
    const editPre = (changes: Partial<typeof preEmployment>) => {
      setCopyNotice(null);
      setPre(changes);
    };

    const method = preEmployment.method || "Urine";
    const labKeys = labsForMethod(method, context);
    const serviceLocked = isLabServiceLocked(preEmployment.collection, preEmployment.labOption);

    const source = tddaPreEmploymentSource(services);

    return (
      <>
        <Divider sx={{ my: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              disabled={frozen}
              checked={preEmployment.included}
              onChange={(e) => {
                // Revealing the block also seeds its confirmation lab for the default method, which
                // was blank on first reveal so every HT pre-employment started invalid (bug 5701).
                const changes: Partial<typeof preEmployment> = { included: e.target.checked };
                if (e.target.checked && !preEmployment.confirmationLab) {
                  changes.confirmationLab = defaultLabForMethod(method, context);
                }
                editPre(changes);
              }}
            />
          }
          label="HT Pre-employment drug test"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          One pre-employment test attached to the medical — no secondary screen, no synthetic
          cannabis screen, no alcohol.
        </Typography>

        {preEmployment.included && (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <TextField
                select
                size="small"
                label="Collection method"
                sx={{ minWidth: 160 }}
                disabled={frozen}
                value={method}
                onChange={(e) =>
                  editPre({
                    method: e.target.value,
                    // The lab re-prefills when the method changes (S2.16).
                    confirmationLab:
                      e.target.value === "OralFluid"
                        ? context.defaultOralFluidLab
                        : context.defaultUrineLab,
                  })
                }
                SelectProps={{ renderValue: renderOptionValue(references.step2Methods) }}
              >
                {/* Only the two screened methods here — hair and nail are sold as service lines,
                    not as a pre-employment collection (S2.16). The detection-window wording is the
                    reference data's, so the client hears the same sentence everywhere (S2.13). */}
                {["Urine", "OralFluid"].map((key) => (
                  <MenuItem key={key} value={key}>
                    {optionMenuContent(references.step2Methods, key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Collection"
                sx={{ minWidth: 160 }}
                disabled={frozen}
                value={preEmployment.collection || "Screen"}
                onChange={(e) => {
                  const collection = e.target.value;
                  const labOption = isCollectOnly(collection)
                    ? COLLECT_ONLY_LAB_OPTIONS.includes(preEmployment.labOption || "")
                      ? preEmployment.labOption
                      : LAB_OPTION_ALWAYS_LAB
                    : preEmployment.labOption;
                  editPre({
                    collection,
                    labOption,
                    labService: defaultLabService(collection, labOption),
                  });
                }}
                SelectProps={{ renderValue: renderOptionValue(references.step2Collections) }}
              >
                {references.step2Collections.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {optionMenuContent(references.step2Collections, option.key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Lab option"
                sx={{ minWidth: 190 }}
                disabled={frozen}
                value={preEmployment.labOption || ""}
                onChange={(e) =>
                  editPre({
                    labOption: e.target.value,
                    labService: defaultLabService(preEmployment.collection, e.target.value),
                  })
                }
                SelectProps={{ renderValue: renderOptionValue(references.step2LabOptions) }}
              >
                {(isCollectOnly(preEmployment.collection)
                  ? COLLECT_ONLY_LAB_OPTIONS
                  : references.step2LabOptions.map((x) => x.key)
                ).map((key) => (
                  <MenuItem key={key} value={key}>
                    {optionMenuContent(references.step2LabOptions, key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Lab service"
                sx={{ minWidth: 200 }}
                disabled={frozen || serviceLocked}
                helperText={labServiceLockReason(preEmployment.collection, preEmployment.labOption)}
                value={preEmployment.labService || ""}
                onChange={(e) => editPre({ labService: e.target.value })}
                SelectProps={{ renderValue: renderOptionValue(references.step2LabServices) }}
              >
                {references.step2LabServices.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {optionMenuContent(references.step2LabServices, option.key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                required
                label="Confirmation laboratory"
                sx={{ minWidth: 220 }}
                disabled={frozen || (method === "OralFluid" && context.oralFluidLabLocked)}
                value={preEmployment.confirmationLab || ""}
                onChange={(e) => editPre({ confirmationLab: e.target.value })}
                SelectProps={{ renderValue: renderOptionValue(references.step2Laboratories) }}
              >
                {labKeys.map((key) => (
                  <MenuItem key={key} value={key}>
                    {optionMenuContent(references.step2Laboratories, key)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Tooltip
                title={
                  source
                    ? ""
                    : "Include a TDDA pre-employment occasion first — there is nothing to copy."
                }
              >
                <span>
                  <Button
                    size="small"
                    disabled={frozen || !source}
                    onClick={() => {
                      if (!source) return;
                      setPre({
                        method: source.method,
                        collection: source.row.collection,
                        labOption: source.row.labOption,
                        labService: source.row.labService,
                        confirmationLab: source.lab,
                      });
                      setCopyNotice(describeCopiedPreEmployment(source, references));
                    }}
                  >
                    Copy from TDDA pre-employment
                  </Button>
                </span>
              </Tooltip>
              {source && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Copies method, collection, lab option, lab service and laboratory from the TDDA{" "}
                  {source.method === "Urine" ? "urine" : "oral fluid"} pre-employment row.
                </Typography>
              )}
              {/* The copy moved five controls at once, so it says which row it read and what each
                  control now holds — the disabled state already explained itself, but a successful
                  copy said nothing at all (bug 5719). */}
              {copyNotice && (
                <Alert
                  severity="success"
                  sx={{ mt: 1 }}
                  onClose={() => setCopyNotice(null)}
                >
                  <Typography variant="caption">{copyNotice}</Typography>
                </Alert>
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={1}
              label="Notes"
              sx={{ mt: 1 }}
              disabled={frozen}
              value={preEmployment.notes ?? ""}
              onChange={(e) => setPre({ notes: e.target.value })}
            />
          </>
        )}
      </>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
      <Typography variant="subtitle2" fontWeight={600}>
        HealthTick services
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        HealthTick is offered to New Zealand clients only.
      </Typography>

      {renderTieredService("PEM")}
      {renderTieredService("AHM")}

      {/* On/off services: no levels, roles or custom notes (S2.14). */}
      <Box sx={{ mt: 1 }}>
        {references.step2HealthTickServices
          .filter((service) => service.key === "FLU" || service.key === "DL9")
          .map((service) => (
            <Box key={service.key}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    disabled={frozen}
                    checked={service.key === "FLU" ? healthTick.fluSelected : healthTick.dl9Selected}
                    onChange={(e) =>
                      set(
                        service.key === "FLU"
                          ? { fluSelected: e.target.checked }
                          : { dl9Selected: e.target.checked },
                      )
                    }
                  />
                }
                label={service.label}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                {service.description}
              </Typography>
            </Box>
          ))}
      </Box>

      {/* Free text for services Imperans cannot hold yet — never required (S2.18). */}
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        label="Other HealthTick services"
        placeholder={context.otherHealthTickServicesPlaceholder}
        disabled={frozen}
        value={healthTick.otherHealthTickServices ?? ""}
        onChange={(e) => set({ otherHealthTickServices: e.target.value })}
      />
      <Typography variant="caption" color="text.secondary">
        Captured for the SOP and the JSON export; no structured service line is created for services
        Imperans cannot hold yet.
      </Typography>
    </Paper>
  );
};

export default HealthTickCard;
